import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Save, Upload, Plus, Trash2, FileText, GripVertical } from 'lucide-react';

const AdminCMSMenuThermes = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [form, setForm] = useState({
    title: 'Menu Thermes',
    subtitle: 'Soirée & Week-end',
    prices: [
      { price: '49€', formula: 'Entrée – Plat – Dessert' },
      { price: '56€', formula: 'Entrée – Plat – Fromage – Dessert' }
    ],
    mise_en_bouche: 'Mise en bouche',
    starters: [{ name: '', description: '', order: 0 }],
    mains: [{ name: '', description: '', order: 0 }],
    cheese: { name: 'Sélection de fromages affinés', description: 'Affinés par notre MOF de la région' },
    dessert_note: 'Dessert au choix à la carte',
    pdf_url: null,
    show_pdf: true,
    is_published: false,
    show_on_website: true
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}` };
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/menu-thermes`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setForm(prev => ({ 
            ...prev, 
            ...data,
            prices: data.prices?.length ? data.prices : prev.prices,
            starters: data.starters?.length ? data.starters : [{ name: '', description: '', order: 0 }],
            mains: data.mains?.length ? data.mains : [{ name: '', description: '', order: 0 }],
            cheese: data.cheese || prev.cheese
          }));
        }
      }
    } catch (error) {
      console.log('No existing menu, starting fresh');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSaving(true);
    try {
      const checkRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/menu-thermes`, { headers });
      const existing = await checkRes.json();
      
      const method = existing ? 'PUT' : 'POST';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/menu-thermes`, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Menu Thermes enregistré" });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const headers = getAuthHeader();
    if (!headers) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/menu-thermes/pdf`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setForm(prev => ({ ...prev, pdf_url: data.pdf_url }));
        toast({ title: "Succès", description: "PDF téléchargé" });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Échec du téléchargement", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updatePrice = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      prices: prev.prices.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const addPrice = () => {
    setForm(prev => ({
      ...prev,
      prices: [...prev.prices, { price: '', formula: '' }]
    }));
  };

  const removePrice = (index) => {
    if (form.prices.length <= 1) return;
    setForm(prev => ({
      ...prev,
      prices: prev.prices.filter((_, i) => i !== index)
    }));
  };

  const addDish = (section) => {
    const maxOrder = Math.max(...form[section].map(d => d.order), -1);
    setForm(prev => ({
      ...prev,
      [section]: [...prev[section], { name: '', description: '', order: maxOrder + 1 }]
    }));
  };

  const updateDish = (section, index, field, value) => {
    setForm(prev => ({
      ...prev,
      [section]: prev[section].map((dish, i) => 
        i === index ? { ...dish, [field]: value } : dish
      )
    }));
  };

  const removeDish = (section, index) => {
    if (form[section].length <= 1) return;
    setForm(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const updateCheese = (field, value) => {
    setForm(prev => ({
      ...prev,
      cheese: { ...prev.cheese, [field]: value }
    }));
  };

  const DishList = ({ title, section, dishes }) => (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => addDish(section)}
          className="flex items-center gap-1"
        >
          <Plus size={16} />
          Ajouter
        </Button>
      </div>
      <div className="space-y-4">
        {dishes.map((dish, index) => (
          <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="text-gray-400 pt-2">
              <GripVertical size={18} />
            </div>
            <div className="flex-1 space-y-2">
              <Input
                value={dish.name}
                onChange={(e) => updateDish(section, index, 'name', e.target.value)}
                placeholder="Nom du plat"
              />
              <Input
                value={dish.description}
                onChange={(e) => updateDish(section, index, 'description', e.target.value)}
                placeholder="Description"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeDish(section, index)}
              className="text-red-500 hover:text-red-700"
              disabled={dishes.length <= 1}
            >
              <Trash2 size={18} />
            </Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Les plats seront présentés avec "ou" entre chaque option
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/cms" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Menu Thermes</h1>
              <p className="text-sm text-gray-500">Menu à prix fixe - Soirée & Week-end</p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Statut du menu</h2>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.is_published}
                onCheckedChange={(v) => updateField('is_published', v)}
              />
              <div>
                <Label className="font-medium">
                  {form.is_published ? 'Publié' : 'Brouillon'}
                </Label>
                <p className="text-xs text-gray-500">
                  {form.is_published ? 'Visible sur le site' : 'Non visible'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.show_on_website}
                onCheckedChange={(v) => updateField('show_on_website', v)}
              />
              <div>
                <Label className="font-medium">Afficher sur le site</Label>
                <p className="text-xs text-gray-500">Section visible dans "Le Soir"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Informations générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Titre du menu</Label>
              <Input
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Input
                value={form.subtitle}
                onChange={(e) => updateField('subtitle', e.target.value)}
                placeholder="Soirée & Week-end"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Prices */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-900">Formules et Prix</h2>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addPrice}
              className="flex items-center gap-1"
            >
              <Plus size={16} />
              Ajouter
            </Button>
          </div>
          <div className="space-y-3">
            {form.prices.map((price, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-24">
                  <Input
                    value={price.price}
                    onChange={(e) => updatePrice(index, 'price', e.target.value)}
                    placeholder="49€"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    value={price.formula}
                    onChange={(e) => updatePrice(index, 'formula', e.target.value)}
                    placeholder="Entrée – Plat – Dessert"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removePrice(index)}
                  className="text-red-500 hover:text-red-700"
                  disabled={form.prices.length <= 1}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Mise en bouche */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Mise en bouche</h2>
          <Input
            value={form.mise_en_bouche}
            onChange={(e) => updateField('mise_en_bouche', e.target.value)}
            placeholder="Mise en bouche"
          />
        </div>

        {/* Dishes */}
        <DishList title="Entrées au choix" section="starters" dishes={form.starters} />
        <DishList title="Plats au choix" section="mains" dishes={form.mains} />

        {/* Cheese */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Fromages</h2>
          <div className="space-y-4">
            <div>
              <Label>Nom</Label>
              <Input
                value={form.cheese.name}
                onChange={(e) => updateCheese('name', e.target.value)}
                placeholder="Sélection de fromages affinés"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={form.cheese.description}
                onChange={(e) => updateCheese('description', e.target.value)}
                placeholder="Affinés par notre MOF de la région"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Dessert note */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Note Dessert</h2>
          <Input
            value={form.dessert_note}
            onChange={(e) => updateField('dessert_note', e.target.value)}
            placeholder="Dessert au choix à la carte"
          />
        </div>

        {/* PDF Upload */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Document PDF</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.show_pdf}
                onCheckedChange={(v) => updateField('show_pdf', v)}
              />
              <Label>Afficher le lien PDF sur le site</Label>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  <Upload size={18} />
                  <span>{uploading ? 'Téléchargement...' : 'Télécharger un PDF'}</span>
                </div>
              </label>
              
              {form.pdf_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${form.pdf_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                >
                  <FileText size={18} />
                  Voir le PDF actuel
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Save Button (mobile) */}
        <div className="md:hidden">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminCMSMenuThermes;
